import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ExtractHttpErrorResponseCode,
  ExtractHttpErrorResponseMessage,
} from '../../../../utils/utils/http-error-response-extractor.util';
import { finalize } from 'rxjs';
import { ERecipientTypeEnum } from '../../../enums/recipient-type.enum';
import { EUserType } from '../../../enums/user-type.enum';
import {
  IAuthEngineHttpBaseResponse,
  ICheckPasswordResetOtpTokenResponseData,
  IResetPasswordRequest,
  IRequestPasswordResetTokenRequest,
  IResetPasswordEventData,
} from '../../../interfaces';
import { AuthService } from '../../../services/auth.service';
import { IremboPasswordRegExpErrorMsg } from '../../../utils/auth.utils';

@Component({
  selector: 'irembogov-non-citizen-admin-portal-reset-password',
  templateUrl: './non-citizen-reset-password.component.html',
})
export class NonCitizenResetPasswordComponent implements OnInit {
  @Input() authorizationServiceBaseUrl!: string;
  @Input() authorizationClientId!: string;
  @Input() showBackToLogin = true;
  @Input() showPasswordStrengthMeter = false;
  @Input() showErrorTitle = false;
  @Input() showTermsCheckbox = false;
  @Input() resetPasswordTitle = 'Forgot password?';
  @Input() resetPasswordDescription =
    'No worries we will send you reset instructions';
  @Input() resetPasswordButtonLabel = 'Reset password';
  @Input() resendLinkLabel = 'Click to resend';
  @Input() resendLinkType: 'link' | 'button' = 'link';
  @Input() resetPasswordRegexErrorMessage: string =
    IremboPasswordRegExpErrorMsg;
  @Input() resetPasswordMatchErrorMessage =
    'Passwords must match before proceeding';
  @Input() accountNotFoundErrorTitle =
    'Sorry, the username you entered does not match any account.';
  @Input() accountNotFoundErrorMessage =
    'Sorry, the username you entered does not match any account. Please check your login details or contact your Account Admin.';
  @Input() accountNotActiveErrorTitle = 'Sorry, your account is inactive.';
  @Input() accountNotActiveErrorMessage =
    'Sorry, your account is inactive. Please contact your Account Admin.';

  @Input() createPasswordTitle = 'Set new password';
  @Input() createPasswordDescription =
    'Password must be at least 8 characters and contain a combination of letters, characters and numbers';
  @Input() createPasswordBtnActionLabel = 'Reset password';

  @Output() OnGoToLogin: EventEmitter<boolean> = new EventEmitter();
  @Input() resetPasswordSuccessTitle = 'Password reset';
  @Input() resetPasswordSuccessDescription =
    'Your password has been successfully reset. Click below to login automatically.';
  @Input() resetPasswordSuccessButtonActionLabel = 'Login';
  @Input() userNameLabel = 'Email Address/Phone Number';
  @Input() userNamePlaceholder = 'Enter your email address';
  @Input() userNameValidationMessage = 'Invalid email address/phone number';

  resetRequestStatus?:
    | 'tokenRequest'
    | 'tokenRequestSent'
    | 'passwordReset'
    | 'success'
    | 'failure';
  isSubmitting = false;
  isVerifyingToken = true;
  oneTimePasswordToken: string;
  username: string;
  noReceiveQuestion = "Didn't receive the email?";
  failureTitle = '';
  failureMessage = '';
  resetPasswordEventData?: IResetPasswordEventData;
  showResendLink = false;
  otpLength = 5;
  otpTitle = "Confirm it's you";
  otpDescription = 'Enter the five-digit code sent to your phone';
  otpInputValidationMessage = `OTP must be ${this.otpLength} digits long`;
  otpPlaceholder = '0';
  usernameTypes = ERecipientTypeEnum;

  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute
  ) {
    this.oneTimePasswordToken =
      this.activatedRoute.snapshot.queryParamMap.get('token') ?? '';
    this.username =
      this.activatedRoute.snapshot.queryParamMap.get('username') ?? '';
  }

  ngOnInit(): void {
    if (this.oneTimePasswordToken && this.username) {
      this.checkActivationOtp();
    } else {
      this.isVerifyingToken = false;
      this.resetRequestStatus = 'tokenRequest';
    }
  }

  checkActivationOtp(): void {
    this.authService
      .checkPasswordResetOtp(
        this.authorizationServiceBaseUrl,
        this.oneTimePasswordToken,
        EUserType.OTHER,
        this.username
      )
      .pipe(finalize(() => (this.isVerifyingToken = false)))
      .subscribe({
        next: (
          response: IAuthEngineHttpBaseResponse<ICheckPasswordResetOtpTokenResponseData>
        ) => {
          if (response.data) {
            this.resetRequestStatus = 'passwordReset';
            return;
          }
          this.resetRequestStatus = 'failure';
        },
        error: (error: HttpErrorResponse) => {
          this.failureTitle = 'Link expired';
          this.failureMessage =
            'The password reset link you are trying to access has expired.';
          this.showResendLink = true;

          if (ExtractHttpErrorResponseCode(error) === 'OTP_EXPIRED') {
            this.failureTitle = 'Link expired';
            this.showResendLink = true;
          }

          this.resetRequestStatus = 'failure';
        },
      });
  }

  onGoToLogin(): void {
    this.OnGoToLogin.emit(true);
  }

  onUsernameFormSubmit($event: IResetPasswordEventData) {
    this.resetPasswordEventData = $event;
    const requestPasswordToken: IRequestPasswordResetTokenRequest = {
      username: $event.username,
      usernameType: $event.usernameType,
      clientId: this.authorizationClientId,
      userType: EUserType.OTHER,
    };

    this.isSubmitting = true;
    this.isVerifyingToken = true;

    this.authService
      .requestPasswordResetToken(
        this.authorizationServiceBaseUrl,
        requestPasswordToken
      )
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.isVerifyingToken = false;
        })
      )
      .subscribe({
        next: () => {
          this.resetRequestStatus = 'tokenRequestSent';
        },
        error: (error: HttpErrorResponse) => {
          console.log(error.error.responseCode);
          if (
            error.error.responseCode === 'PASSWORD_RESET_ERROR_ACCOUNT_INACTIVE'
          ) {
            this.resetRequestStatus = 'tokenRequestSent';
          } else {
            this.failureTitle = 'Password reset request failed';
            this.failureMessage = ExtractHttpErrorResponseMessage(
              error,
              'Could not request for a password reset link'
            );
            this.resetRequestStatus = 'failure';
          }
        },
      });
  }

  onPasswordFormSubmit($event: string) {
    const requestPasswordRequest: IResetPasswordRequest = {
      oneTimePasswordToken: this.oneTimePasswordToken || '',
      newPassword: $event,
      clientId: this.authorizationClientId,
      userType: EUserType.OTHER,
      username: this.username,
    };

    this.isSubmitting = true;

    this.authService
      .resetPassword(this.authorizationServiceBaseUrl, requestPasswordRequest)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.resetRequestStatus = 'success';
        },
        error: error => {
          this.failureTitle = 'Reset password failed';
          this.failureMessage = ExtractHttpErrorResponseMessage(
            error,
            'Could not reset password'
          );
        },
      });
  }

  resetToRequestNewToken() {
    this.resetRequestStatus = 'tokenRequest';
    this.isVerifyingToken = false;
  }

  onOtpSubmit($event: string) {
    this.isSubmitting = true;
    this.isVerifyingToken = true;

    const encodedOtp = this.getEncodedString($event + '');
    const encodedPhoneNumber = this.getEncodedString(
      this.resetPasswordEventData?.username || ''
    );

    this.oneTimePasswordToken = encodedOtp;
    this.username = encodedPhoneNumber;

    this.authService
      .checkPasswordResetOtp(
        this.authorizationServiceBaseUrl,
        encodedOtp,
        EUserType.OTHER,
        encodedPhoneNumber
      )
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.isVerifyingToken = false;
        })
      )
      .subscribe({
        next: (
          response: IAuthEngineHttpBaseResponse<ICheckPasswordResetOtpTokenResponseData>
        ) => {
          if (response.data) {
            this.resetRequestStatus = 'passwordReset';
            this.failureTitle = '';
            this.failureMessage = '';
            return;
          }
          this.resetRequestStatus = 'failure';
        },
        error: (error: HttpErrorResponse) => {
          this.failureTitle = 'Invalid OTP';
          this.failureMessage = ExtractHttpErrorResponseMessage(
            error,
            'Invalid OTP'
          );
        },
      });
  }

  getEncodedString(str: string): string {
    let encoded = '';
    try {
      encoded = btoa(str ?? '');
    } catch (error) {
      console.log(error);
    }

    return encoded;
  }
}
